import { createContext, useContext, useState, useCallback } from "react";
import { User, Notification, NotificationType } from "@/models/models";

export interface AppActionButton {
    label: string
    onClick: () => void
    appearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent'
    working: boolean
    icon?: React.ReactNode
}

export interface AddNotificationOptions {
    type: NotificationType;
    title: string;
    message: string;
    autoDismiss?: boolean;
    autoDismissDelay?: number;
}

interface AppContextType {
    fetchData: <T>(url: string, body?: any, headers?: any) => Promise<T>;
    user: User | null;
    setUser: (user: User | null) => void;
    accessToken: string | null;
    setAccessToken: (accessToken: string | null) => void;
    currentPath: string | null;
    setCurrentPath: (path: string | null) => void;
    dialog: React.ReactNode | null;
    setDialog: (dialog: React.ReactNode | null) => void;
    actionButtons: AppActionButton[];
    setActionButtons: (actionButtons: AppActionButton[]) => void;
    pageTitle: string;
    setPageTitle: (title: string) => void;
    // Notification system
    notifications: Notification[];
    addNotification: (options: AddNotificationOptions) => string;
    dismissNotification: (id: string) => void;
    dismissAllNotifications: () => void;
}

export const AppContext = createContext<AppContextType>({
    fetchData: async () => { throw new Error("AppContext not initialized"); },
    user: null,
    setUser: () => {},
    accessToken: null,
    setAccessToken: () => {},
    currentPath: null,
    setCurrentPath: () => {},
    dialog: null,
    setDialog: () => {},
    actionButtons: [],
    setActionButtons: () => {},
    pageTitle: '',
    setPageTitle: () => {},
    // Notification system defaults
    notifications: [],
    addNotification: () => '',
    dismissNotification: () => {},
    dismissAllNotifications: () => {}
});

// Helper to generate unique IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
    const [ user, setUser ] = useState<User | null>(null);
    const [ accessToken, setAccessToken ] = useState<string | null>(null);
    const [ currentPath, setCurrentPath ] = useState<string | null>(null);
    const [ dialog, setDialog ] = useState<React.ReactNode | null>(null);
    const [ actionButtons, setActionButtons ] = useState<AppActionButton[]>([]);
    const [ pageTitle, setPageTitle ] = useState<string>('');
    const [ notifications, setNotifications ] = useState<Notification[]>([]);

    // Add a new notification
    const addNotification = useCallback((options: AddNotificationOptions): string => {
        const id = generateId();
        const notification: Notification = {
            id,
            type: options.type,
            title: options.title,
            message: options.message,
            timestamp: new Date(),
            autoDismiss: options.autoDismiss ?? (options.type !== 'error'),
            autoDismissDelay: options.autoDismissDelay ?? 3000,
            dismissed: false
        };

        setNotifications(prev => [notification, ...prev]);

        // Set up auto-dismiss timer if applicable
        if (notification.autoDismiss) {
            setTimeout(() => {
                setNotifications(prev =>
                    prev.map(n => n.id === id ? { ...n, dismissed: true } : n)
                );
            }, notification.autoDismissDelay);
        }

        return id;
    }, []);

    // Dismiss a single notification
    const dismissNotification = useCallback((id: string) => {
        setNotifications(prev =>
            prev.map(n => n.id === id ? { ...n, dismissed: true } : n)
        );
    }, []);

    // Dismiss all notifications
    const dismissAllNotifications = useCallback(() => {
        setNotifications(prev =>
            prev.map(n => ({ ...n, dismissed: true }))
        );
    }, []);

    const fetchData = useCallback(async <T,>(url: string, body?: any, headers?: any): Promise<T> => {
        try {
            const authHeaders = accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {};

            // Prepare request options dynamically so we can handle different body types.
            const requestHeaders: Record<string, string> = {
                ...headers,
                ...authHeaders,
            };

            const requestInit: RequestInit = {
                method: 'POST',
                credentials: 'include',
                headers: requestHeaders,
            };

            if (body !== undefined) {
                if (body instanceof FormData) {
                    // Let the browser set the correct multipart boundary header.
                    requestInit.body = body;
                } else {
                    requestInit.body = JSON.stringify(body);
                    requestHeaders['Content-Type'] = 'application/json';
                }
            }

            const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}${url}`, requestInit);

            const responseJson = await response.json();

            // Handle both HTTP errors and application-level errors
            if (!response.ok || responseJson.status === 'error') {
                const errorMessage = responseJson.message || `HTTP error: ${response.status}`;
                addNotification({
                    type: 'error',
                    title: 'Error',
                    message: errorMessage,
                    autoDismiss: false
                });
                return { status: 'error', message: errorMessage } as T;
            }

            return responseJson as T;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
            addNotification({
                type: 'error',
                title: 'Error',
                message: errorMessage,
                autoDismiss: false
            });
            return { status: 'error', message: errorMessage } as T;
        }
    }, [accessToken, addNotification]);

    return (
        <AppContext.Provider value={{
            fetchData,
            user,
            setUser,
            accessToken,
            setAccessToken,
            currentPath,
            setCurrentPath,
            dialog,
            setDialog,
            actionButtons,
            setActionButtons,
            pageTitle,
            setPageTitle,
            notifications,
            addNotification,
            dismissNotification,
            dismissAllNotifications
        }}>
            {children}
        </AppContext.Provider>
    );
};

export const useAppContext = () => useContext(AppContext);
